import { Dispatch, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Grid, Select } from '@material-ui/core';
import classNames from 'classnames';
import { Action } from 'redux';

import { OrderSideEnum } from '../../../../lib/enums/order-side.enum';
import { OrderStatusEnum } from '../../../../lib/enums/order-status.enum';
import { OrderTypeEnum } from '../../../../lib/enums/order-type.enum';
import lang from '../../../../lib/language';
import { SOROrderTimeInForce } from '../../../../lib/models/gateway/types';
import { Order } from '../../../../lib/models/trading/types';
import { getUnreadMessageCount } from '../../../../lib/store/ams';
import { getBuyingPowerFromOMS as getBuyingPowerFromOMSCall } from '../../../../lib/store/common-actions';
import { RootState } from '../../../../lib/store/index';
import {
  getAccountSummary,
  getAccountTradeHistory,
  getOpenPositions as getPositions,
  setHasOrderUpdate,
} from '../../../../lib/store/reporting';
import { PENDING_ORDERS_REQUEST_TENNSTATUS } from '../../../../lib/store/reporting/constants';
import { AccountTradeHistoryPayloadData } from '../../../../lib/store/reporting/types';
import { modifyOrder as serviceModifyOrder, resetRequestStatus } from '../../../../lib/store/trading/index';
import MarketStateCache from '../../../../lib/store-util/MarketStateCache';
import SubscribeGroupCache from '../../../../lib/store-util/SubscribeGroupCache';
import SymbolStatusCache from '../../../../lib/store-util/SymbolStatusCache';
import { isOneOf } from '../../../../lib/util/DataHelpers';
import { checkIfSymbolIsHalted } from '../../../../lib/util/HaltSymbolHelper';
import { isMarketClosed } from '../../../../lib/util/MarketDataHelpers';
import { mapOrderSORtoSide, orderStatusToCallStatus } from '../../../../lib/util/TradingHelpers';

import BuyAndSellDot from '../../../../assets/img/BuyAndSellDot.png';
import cancelIcon from '../../../../assets/img/close-circle.png';
import DeleteIcon from '../../../../assets/img/delete.png';
import modifyIcon from '../../../../assets/img/edit.png';
import EditIcon from '../../../../assets/img/edit_pen.png';
import editIcon from '../../../../assets/img/icon-edit.png';
import { useIsMobile } from '../../../../hooks';
import BaseModal from '../../../../shared_components/BaseModal/BaseModal';
import Button from '../../../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../../../shared_components/Button/Button.types';
import { UI_ROUTES } from '../../../../util/routes';
import { CapitalBuySellButtonsConditions, ClickableDiv } from '../../..';
import CancelOrder from '../../../OrderActions/CancelOrder/CancelOrder';
import HaltedSymbol from '../../../OrderActions/HaltedSymbol/HaltedSymbol';
import ModifyOrder from '../../../OrderActions/ModifyOrder/ModifyOrder';
import {
  getCancelTitle,
  getModifyOrderTitle,
  modifyOrderPopUpBody,
  modifyOrderPopUpTitle,
  proccessPartiallyRejectedMessage,
} from '../../../OrderActions/OrderHelpers';
import useCancelOrderTimer from '../../../OrderActions/useCancelOrderTimer';
import useNewOrderTimer from '../../../OrderActions/useNewOrderTimer';
import ProcessFinishedModal from '../../../ProcessFinishedModal/ProcessFinishedModal';

import styles from './ButtonsCancelAndModify.module.scss';

type Props = {
  isSwipeable?: boolean
  row: any
  useDots?: boolean
  showModifyButtons?: boolean
} & ReduxProps

const ButtonsCancelAndModify = (props: Props) => {
  const {
    row,
    isSwipeable,
    requestStatus,
    submittedOrder,
    showModifyButtons,
    useDots,
    getOrders,
    getOpenPositions,
    modifyOrder,
    myStocksUpdate,
    resetStatus,
    getAccSummary,
    getUnreadCount,
    getBuyingPowerFromOMS,
  } = props;

  const { isMobile } = useIsMobile();
  const navigate = useNavigate();

  const [order, setOrder] = useState<Order>({});
  const [orderType, setOrderType] = useState<string>('');
  const [isHalted, setIsHalted] = useState<boolean>(false);
  const [orderSide, setOrderSide] = useState<OrderSideEnum>(OrderSideEnum.Buy);
  const [showModifyOrder, setShowModifyOrder] = useState<boolean>(false);
  const [showModalCancel, setShowModalCancel] = useState<boolean>(false);
  const [showResultModifyModal, setShowResultModifyModal] = useState<boolean>(false);
  const [showResultModalCancel, setShowResultModalCancel] = useState<boolean>(false);

  const { marketState } = MarketStateCache.use();
  const marketClosed = isMarketClosed(marketState);
  const { id, name, side, plainType, totalQuantity, partialFillQuantity, initialPrice } = row;
  const symbolsStatusCache = SymbolStatusCache.use(name);
  const { status, ordType, side: ordSide, orderQty, filledSoFar } = submittedOrder;
  const finalRejectedOrder = requestStatus === OrderStatusEnum.FinalReject;
  const orderInProgress = requestStatus === OrderStatusEnum.InProgress;
  const partiallyRejectedOrder = status === OrderStatusEnum.PartiallyReject;
  const showResultModal = [OrderStatusEnum.Accepted, OrderStatusEnum.Rejected].includes(requestStatus);

  const {
    orderTimeoutPassed, showFillOrder, hasNoResponse,
  } = useNewOrderTimer(showResultModifyModal && showResultModal, OrderTypeEnum.Limit, status, requestStatus);
  const {
    cancelOrderTimeoutSuccess, hasNoCancelResponse,
  } = useCancelOrderTimer(showResultModalCancel && showResultModal, status, requestStatus);
  const showModifyOrderModal = (orderTimeoutPassed || (hasNoResponse && showResultModifyModal)
  || (finalRejectedOrder && showResultModifyModal));
  const showCancelOrderModal = (cancelOrderTimeoutSuccess || (hasNoCancelResponse && showResultModalCancel)
  || (finalRejectedOrder && showResultModalCancel));

  const onCancelClicked = () => {
    setShowModalCancel(true);
    SubscribeGroupCache.setComponent('initial');
  };

  const handleModalModify = () => {
    if (checkIfSymbolIsHalted(symbolsStatusCache)) {
      setIsHalted(true);
      return;
    }
    setOrderType(plainType);
    setOrderSide(mapOrderSORtoSide(side));

    if (isMobile) {
      navigate(`${UI_ROUTES.modify}`, {
        state: {
          symbol: name,
          parentOrderId: id,
          orderSide: mapOrderSORtoSide(side),
          orderType: plainType,
          initialPrice,
          tradingSessionId: row.tradingSessionId,
          initialTimeInForce: SOROrderTimeInForce[row.timeInForce],
          orderQuantity: totalQuantity - partialFillQuantity,
          origin: window.location.pathname,
        },
        replace: true,
      });
    } else {
      setShowModifyOrder(true);
    }
  };

  useEffect(() => {
    if (showModifyOrderModal) {
      getBuyingPowerFromOMS();
    }
  }, [showModifyOrderModal]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showResultModifyModal) {
      modifyOrder(order);
    }
  }, [modifyOrder, order, showResultModifyModal]);

  const handleModalCancel = (isConfirmed) => {
    if (isConfirmed) {
      setShowResultModalCancel(true);
    }
    SubscribeGroupCache.setComponent('initial');
    setShowModalCancel(false);
  };

  const handleCloseModifyModal = () => {
    SubscribeGroupCache.setComponent('initial');
    setShowModifyOrder(false);
  };

  const onCloseOrderResultModal = () => {
    if (orderInProgress) {
      resetStatus();
    }
    getOrders({ tenNStatuses_StringList: PENDING_ORDERS_REQUEST_TENNSTATUS, isPendingOrdersCall: true });
  };

  const onCloseCancelResultModal = () => {
    onCloseOrderResultModal();
    setShowResultModalCancel(false);
    if (isOneOf(status, [
      OrderStatusEnum.Filled,
      OrderStatusEnum.PartiallyFilled,
      OrderStatusEnum.Rejected,
      OrderStatusEnum.Expired,
    ])) {
      getUnreadCount();
    }
    SubscribeGroupCache.setComponent('initial');
  };

  const onCloseModifyResultModal = () => {
    onCloseOrderResultModal();
    if (!marketClosed) {
      getOpenPositions();

      if (submittedOrder?.status === OrderStatusEnum.Filled) {
        getAccSummary();
      }
      if (isOneOf(status, [
        OrderStatusEnum.Filled,
        OrderStatusEnum.PartiallyFilled,
        OrderStatusEnum.Rejected,
        OrderStatusEnum.Expired,
      ])) {
        getUnreadCount();
      }
    }
    SubscribeGroupCache.setComponent('initial');
    setShowResultModifyModal(false);
    myStocksUpdate(false);
  };

  const handleSelectIcon = () => (
    <img
      src={BuyAndSellDot}
      className={styles.dotIcon}
      alt="img"
    />
  );

  return (
    <>
      {isMobile ? (
        <CapitalBuySellButtonsConditions noContent>
          <div className={isSwipeable ? styles.buttonWrapperSwipable : styles.buttonWrapper}>
            <Button
              className={classNames(
                styles.cancelButtonMobile,
              )}
              variant={ButtonTypesEnum.Link}
              onClick={() => onCancelClicked()}
            >
              <img src={cancelIcon} alt="cancel icon" />
            </Button>
            <Button
              className={classNames(
                styles.editButtonMobile,
              )}
              variant={ButtonTypesEnum.Link}
              onClick={() => handleModalModify()}
            >
              <img src={editIcon} alt="edit icon pink" />
            </Button>
          </div>
        </CapitalBuySellButtonsConditions>
      ) : (
        <CapitalBuySellButtonsConditions noContent>
          { useDots ? (
            <div className={styles.wrapperButtons}>
              {showModifyButtons && (
                <Select
                  disableUnderline
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    PaperProps: {
                      style: {
                        backgroundColor: '#191C2E',
                        color: '#FFFFFF',
                        borderRadius: 12,
                      },
                    },
                    getContentAnchorEl: null,
                  }}
                  IconComponent={handleSelectIcon}
                >
                  <ClickableDiv
                    className={styles.actionButton}
                    onClick={onCancelClicked}
                  >
                    <img
                      src={DeleteIcon}
                      alt="buy"
                    />
                    <span className={styles.text}>
                      {lang.commonCancelButton()}
                    </span>
                  </ClickableDiv>
                  <ClickableDiv
                    className={styles.actionButton}
                    onClick={handleModalModify}
                  >
                    <img
                      src={EditIcon}
                      className={styles.image}
                      alt="sell"
                    />
                    <span className={styles.text}>
                      {lang.commonModify()}
                    </span>
                  </ClickableDiv>
                </Select>
              )}
            </div>
          ) : (
            <Grid className={styles.containerButtons}>
              <Grid className={styles.wrapperButtons}>
                <button
                  type="button"
                  className={classNames(styles.modifyButton, styles.cancelButton)}
                  onClick={() => onCancelClicked()}
                >
                  <>
                    <img src={cancelIcon} className={styles.cancelIcon} alt="cancel icon" />
                    <span className={styles.cancelText}>{lang.commonCancelButton()}</span>
                  </>
                </button>
                <button
                  type="button"
                  className={classNames(styles.modifyButton, styles.editButton)}
                  onClick={() => handleModalModify()}
                >
                  <>
                    <img src={modifyIcon} className={styles.modifyIcon} alt="modify icon" />
                    <span className={styles.modifyText}>{lang.commonModify()}</span>
                  </>
                </button>
              </Grid>
            </Grid>
          )}
        </CapitalBuySellButtonsConditions>
      )}
      {isHalted && <HaltedSymbol symbolName={row.companyName ?? name} toggleModal={() => setIsHalted(false)} />}
      {showModalCancel && (
      <BaseModal
        customStyle={
        isMobile
          ? {
            paddingBottom: 32,
            margin: 0,
            width: window.innerWidth,
            marginTop: window.innerHeight / 2.6,
            position: 'fixed',
            alignSelf: 'flex-end',
          }
          : undefined
      }
        contentClassName={isMobile ? styles.cancelOrderModalContentMobile : undefined}
        isOpen={showModalCancel}
      >
        <CancelOrder orderId={id} handleModalCancel={handleModalCancel} />
      </BaseModal>
      )}
      <BaseModal
        isOpen={showModifyOrder}
        toggle={handleCloseModifyModal}
      >
        <ModifyOrder
          symbol={name}
          parentOrderId={id}
          orderSide={orderSide}
          orderType={orderType}
          initialPrice={initialPrice}
          tradingSessionId={row.tradingSessionId}
          initialTimeInForce={SOROrderTimeInForce[row.timeInForce]}
          orderQuantity={totalQuantity - partialFillQuantity}
          setOrder={(value) => setOrder(value)}
          setModifyOrderRequested={(value) => {
            setShowModifyOrder(false);
            setShowResultModifyModal(value);
          }}
          onCloseModal={handleCloseModifyModal}
        />
      </BaseModal>
      {showCancelOrderModal && !isMobile && (
      <ProcessFinishedModal
        symbol={name}
        isCancelOrder
        callStatus={orderStatusToCallStatus(status as OrderStatusEnum)}
        processSuccessMessageTitle={getCancelTitle(side, orderType)}
        processSuccessMessageBody={
          lang.orderMessageCancelSuccessBody(orderQty ?? 0, name)
        }
        processFailedMessage={lang.orderMessageBodyCancelFail()}
        processFailedMessageSub={lang.orderPopupFailResultSubTitle()}
        processPendingMessage={
          status === OrderStatusEnum.PendingCancelOrModify ? {
            title: getCancelTitle(side, orderType),
            body: lang.orderMessageBodyCancelPending(orderQty!, name),
          } : undefined
        }
        defaultMessageTitle={orderInProgress ? getCancelTitle(side, orderType) : ''}
        defaultMessageBody={orderInProgress ? lang.orderMessageBodyCancelPending(orderQty!, name) : ''}
        toggleModal={onCloseCancelResultModal}
      />
      )}
      {showModifyOrderModal && !isMobile && (
      <ProcessFinishedModal
        symbol={name}
        processSuccessMessageTitle={modifyOrderPopUpTitle(
          orderSide, showFillOrder, name, ordType!, status, order.orderQty,
        )}
        processFailedMessage={lang.orderMessageBodyModifyFail()}
        processFailedMessageSub={lang.orderPopupFailResultSubTitle()}
        processPendingMessage={
          status === OrderStatusEnum.PendingCancelOrModify ? {
            title: getModifyOrderTitle(side, orderType),
            body: lang.orderMessageBodyModifyPending2(orderQty!, name),
          } : undefined
        }
        processPartiallyRejectedMessage={partiallyRejectedOrder
          ? proccessPartiallyRejectedMessage(name, ordType!, ordSide!, orderQty!, filledSoFar) : undefined}
        processSuccessMessageBody={
          modifyOrderPopUpBody(showFillOrder, name, filledSoFar, status, order.orderQty)
        }
        defaultMessageTitle={orderInProgress ? getModifyOrderTitle(side, orderType) : ''}
        defaultMessageBody={orderInProgress ? lang.orderMessageBodyModifyPending2(orderQty!, name) : ''}
        callStatus={orderStatusToCallStatus(status as OrderStatusEnum)}
        toggleModal={onCloseModifyResultModal}
      />
      )}
    </>
  );
};

const mapStateProps = (state: RootState) => ({
  requestStatus: state.trading.requestStatus,
  submittedOrder: state.trading.order,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  resetStatus: () => dispatch(resetRequestStatus()),
  modifyOrder: (order: Order) => dispatch(serviceModifyOrder(order)),
  getOrders: (data: AccountTradeHistoryPayloadData) => dispatch(getAccountTradeHistory(data)),
  getOpenPositions: (symbol?: string) => dispatch(getPositions({ symbol })),
  myStocksUpdate: (hasUpdate: boolean) => dispatch(setHasOrderUpdate(hasUpdate)),
  getAccSummary: () => dispatch(getAccountSummary()),
  getUnreadCount: () => dispatch(getUnreadMessageCount()),
  getBuyingPowerFromOMS: () => dispatch(getBuyingPowerFromOMSCall()),
});

const connector = connect(mapStateProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(ButtonsCancelAndModify);
